import { useState, useCallback } from 'react';
import { apiClient } from '../config/api';

export const useAssets = (user, token, showMessage) => {
  const [assets, setAssets] = useState([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  // Forms and selected state
  const [assetForm, setAssetForm] = useState({
    name: '',
    description: '',
    asset_type: '',
    value: 0,
    serial_number: '',
  });

  const [editingAsset, setEditingAsset] = useState(null);
  const [editAssetForm, setEditAssetForm] = useState({
    name: '',
    description: '',
    asset_type: '',
    value: 0,
    serial_number: '',
    assigned_to: '',
  });

  const [selectedAssetDetail, setSelectedAssetDetail] = useState(null);
  const [showAssetDetailModal, setShowAssetDetailModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Fetch all assets
  const fetchAssets = useCallback(async () => {
    if (!token) return;
    try {
      const response = await apiClient('/api/assets/');
      if (response.ok) {
        const data = await response.json();
        setAssets(data);
      }
    } catch (error) {
      console.error('Fetch assets error:', error);
    }
  }, [token]);

  // Create asset
  const handleCreateAsset = async (e) => {
    if (e) e.preventDefault();
    setIsLoadingAssets(true);

    try {
      const formData = new FormData();
      formData.append('name', assetForm.name);
      formData.append('description', assetForm.description);
      formData.append('asset_type', assetForm.asset_type);
      formData.append('value', assetForm.value.toString());
      formData.append('serial_number', assetForm.serial_number);
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await apiClient('/api/assets/', {
        method: 'POST',
        isFormData: true,
        body: formData,
      });

      if (response.ok) {
        await fetchAssets();
        setAssetForm({ name: '', description: '', asset_type: '', value: 0, serial_number: '' });
        setSelectedFile(null);
        if (showMessage) showMessage('Asset created successfully!', 'success');
        return true;
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to create asset', 'error');
        return false;
      }
    } catch (error) {
      if (showMessage) showMessage('Network error creating asset', 'error');
      return false;
    } finally {
      setIsLoadingAssets(false);
    }
  };

  // Assign asset to user
  const handleAssignAsset = async (assetId, userId) => {
    setIsLoadingAssets(true);
    try {
      const response = await apiClient(`/api/assets/${assetId}/assign`, {
        method: 'POST',
        body: { user_id: userId || null },
      });

      if (response.ok) {
        await fetchAssets();
        if (showMessage) showMessage('Asset assignment updated!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to assign asset', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error assigning asset', 'error');
    } finally {
      setIsLoadingAssets(false);
    }
  };

  // Delete asset
  const handleDeleteAsset = async (assetId, assetName) => {
    if (!window.confirm(`Are you sure you want to delete "${assetName || 'this asset'}"?`)) {
      return;
    }

    setIsLoadingAssets(true);
    try {
      const response = await apiClient(`/api/assets/${assetId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchAssets();
        if (showMessage) showMessage('Asset deleted successfully!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to delete asset', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error deleting asset', 'error');
    } finally {
      setIsLoadingAssets(false);
    }
  };

  // Edit asset
  const handleEditAsset = async (e) => {
    if (e) e.preventDefault();
    if (!editingAsset) return;

    setIsLoadingAssets(true);
    try {
      const formData = new FormData();
      formData.append('name', editAssetForm.name);
      formData.append('description', editAssetForm.description);
      formData.append('asset_type', editAssetForm.asset_type);
      formData.append('value', editAssetForm.value.toString());
      formData.append('serial_number', editAssetForm.serial_number);
      if (editAssetForm.assigned_to) {
        formData.append('assigned_to', editAssetForm.assigned_to);
      }
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await apiClient(`/api/assets/${editingAsset.id}`, {
        method: 'PUT',
        isFormData: true,
        body: formData,
      });

      if (response.ok) {
        await fetchAssets();
        setEditingAsset(null);
        setSelectedFile(null);
        if (showMessage) showMessage('Asset updated successfully!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to update asset', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error updating asset', 'error');
    } finally {
      setIsLoadingAssets(false);
    }
  };

  const handleAssetClick = (asset) => {
    setSelectedAssetDetail(asset);
    setShowAssetDetailModal(true);
  };

  return {
    assets,
    isLoadingAssets,
    assetForm,
    setAssetForm,
    editingAsset,
    setEditingAsset,
    editAssetForm,
    setEditAssetForm,
    selectedAssetDetail,
    setSelectedAssetDetail,
    showAssetDetailModal,
    setShowAssetDetailModal,
    selectedFile,
    setSelectedFile,
    fetchAssets,
    handleCreateAsset,
    handleAssignAsset,
    handleDeleteAsset,
    handleEditAsset,
    handleAssetClick,
  };
};
